import { Component, effect, inject, model, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PersonalParamDto } from '@shared/interfaces/dtos/params';
import { UpdatePersonalParams } from '@shared/interfaces/params';
import { PersonalParamsService } from '@shared/services/personal-params.service';
import { finalize } from 'rxjs';
import { NotificationSuccess } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';

@Component({
  selector: 'idel-saved-searches',
  templateUrl: './saved-searches.component.html',
  styleUrl: './saved-searches.component.scss',
})
export class SavedSearchesComponent implements OnInit, OnDestroy {
  private readonly notifier = inject(Notifier);
  private readonly personalParamsService = inject(PersonalParamsService);

  public readonly refresh = model.required<boolean>();
  private readonly refreshEff = effect(
    () => {
      const refresh = this.refresh();

      if (refresh) {
        this.fetchSavedSearches();
        this.refresh.set(false);
      }
    },
    { allowSignalWrites: true }
  );

  public readonly onClick = output<PersonalParamDto>();
  public readonly refreshDashboard = output<void>();

  public readonly fetchingSavedSearches = signal<boolean>(false);
  public readonly savedSearches = signal<PersonalParamDto[]>([]);

  public readonly savedSearchToUpdate = signal<PersonalParamDto | null>(null);
  public readonly savedSearchToDelete = signal<PersonalParamDto | null>(null);
  public readonly isSavedSearchUpdateDialogOpen = signal<boolean>(false);
  public readonly isDeleteSavedSearchConfirmDialogOpen = signal<boolean>(false);

  public readonly updateSavedSearchFormGroup = new FormGroup({
    description: new FormControl<string | null>(null, Validators.required),
    hideInDashboard: new FormControl<boolean>(false),
    order: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    this.fetchSavedSearches();
  }

  ngOnDestroy(): void {
    this.refreshEff.destroy();
  }

  private fetchSavedSearches(): void {
    this.fetchingSavedSearches.set(true);

    this.personalParamsService
      .fetchPersonalParams('savedSearches')
      .pipe(finalize(() => this.fetchingSavedSearches.set(false)))
      .subscribe({
        next: (params) => this.savedSearches.set([...params]),
      });
  }

  public onEditSavedSearch(search: PersonalParamDto): void {
    this.populateForm(search);
    this.savedSearchToUpdate.set(search);
    this.isSavedSearchUpdateDialogOpen.set(true);
  }

  private populateForm(search: PersonalParamDto): void {
    this.updateSavedSearchFormGroup.setValue({
      description: search.description,
      hideInDashboard: search.hideInDashboard ?? false,
      order: search.displayOrder ?? null,
    });
  }

  public cancelUpdate(): void {
    this.savedSearchToUpdate.set(null);
    this.updateSavedSearchFormGroup.reset();
    this.isSavedSearchUpdateDialogOpen.set(false);
  }

  public updateSavedSearch(): void {
    if (this.updateSavedSearchFormGroup.invalid) return;

    const paramToUpdate = this.savedSearchToUpdate();

    if (!paramToUpdate) return;

    const body: UpdatePersonalParams = {
      name: paramToUpdate.name,
      description: this.updateSavedSearchFormGroup.controls.description.value ?? '',
      value: paramToUpdate.value,
      shared: paramToUpdate.shared,
      displayOrder: this.updateSavedSearchFormGroup.controls.order.value ?? -1,
      hideInDashboard: this.updateSavedSearchFormGroup.controls.hideInDashboard.value,
    };

    this.personalParamsService
      .updatePersonalParam({ scope: 'savedSearches', paramId: paramToUpdate.id, body })
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Recherche sauvée modifiée' }));
          this.cancelUpdate();
          this.fetchSavedSearches();
          this.refreshDashboard.emit();
        },
      });
  }

  public onDeleteSavedSearch(search: PersonalParamDto): void {
    this.savedSearchToDelete.set(search);
    this.isDeleteSavedSearchConfirmDialogOpen.set(true);
  }

  public deleteSavedSearch(): void {
    const paramToDelete = this.savedSearchToDelete();

    if (!paramToDelete) return;

    this.personalParamsService.deletePersonalParam({ scope: 'savedSearches', paramId: paramToDelete.id }).subscribe({
      next: () => {
        this.notifier.showSuccess(new NotificationSuccess({ message: 'Recherche sauvée supprimée' }));
        this.isDeleteSavedSearchConfirmDialogOpen.set(false);
        this.savedSearchToDelete.set(null);
        this.fetchSavedSearches();
        this.refreshDashboard.emit();
      },
    });
  }
}
